<template>
  <div class="container py-4">
    <b-form>
      <b-row>
        <b-col cols="12">
          <b-row>
            <!-- Sol: Form -->
            <b-col cols="12" lg="6" class="mb-4">
              <b-row class="align-items-center mb-3">
                <b-col cols="12" md="4" class="text-right font-weight-bold">
                  Kart Numarası:
                </b-col>
                <b-col cols="12" md="8">
                  <b-form-input
                      v-model="cardNumber"
                      @input="formatCardNumber"
                      maxlength="19"
                      placeholder="#### #### #### ####"
                      inputmode="numeric"
                      @keypress="onlyAllowNumbers"
                      class="rounded-pill"
                  />
                </b-col>
              </b-row>

              <b-row class="align-items-center mb-3">
                <b-col cols="12" md="4" class="text-right font-weight-bold">
                  Ad Soyad:
                </b-col>
                <b-col cols="12" md="8">
                  <b-form-input
                      v-model="cardName"
                      maxlength="50"
                      class="rounded-pill"
                  />
                </b-col>
              </b-row>

              <b-row class="align-items-center mb-3">
                <b-col cols="12" md="4" class="text-right font-weight-bold">
                  Ay / Yıl:
                </b-col>

                <b-col cols="12" md="4" class="d-flex align-items-center mb-2 mb-md-0">
                  <b-form-select
                      v-model="expiryMonth"
                      :options="monthOptions"
                      class="rounded-pill expanded-centered-input mr-1"
                      placeholder="Ay"
                  />
                  <b-form-select
                      v-model="expiryYear"
                      :options="yearOptions"
                      class="rounded-pill expanded-centered-input mr-1"
                      placeholder="Yıl"
                  />
                </b-col>

                <b-col cols="12" md="2" class="text-right font-weight-bold mt-2 mt-md-0">
                  CVV:
                </b-col>

                <b-col cols="12" md="2">
                  <b-form-input
                      v-model="cvv"
                      maxlength="3"
                      @focus="isCvvFocused = true"
                      @blur="isCvvFocused = false"
                      placeholder="***"
                      inputmode="numeric"
                      @input="cvv = cvv.replace(/\D/g, '')"
                      @keypress="onlyAllowNumbers"
                      class="rounded-pill expanded-centered-input"
                  />
                </b-col>
              </b-row>

              <b-row class="align-items-center mb-3">
                <b-col cols="12" md="4" class="text-right font-weight-bold">
                  Miktar:
                </b-col>

                <b-col cols="12" md="4" class="text-right font-weight-bold mt-2 mt-md-0">
                  <b-form-input
                      type="text"
                      :value="formattedAmount"
                      @input="onInputAmount"
                      @keypress="onlyAllowNumbersForAmount"
                      placeholder="0,00"
                      name="amount"
                      step="0.01"
                      min="0.01"
                      class="rounded-pill expanded-centered-input w-100"
                      required
                  />
                </b-col>
              </b-row>

              <b-button
                  type="submit"
                  variant="success"
                  class="mt-4 px-4 py-2 rounded-pill float-right"
                  @click.prevent="submitPayment"
              >
                Ödemeyi Tamamla
              </b-button>
            </b-col>

            <!-- Sağ: Kart animasyonu -->
            <b-col cols="12" lg="6" class="mb-4">
              <div class="card-wrapper">
                <div :class="['card-inner', { flipped: isCvvFocused }]">
                  <!-- Ön yüz -->
                  <div class="card-face card-front">
                    <div class="card-header">
                      <img
                          v-if="cardType === 'visa'"
                          src="@/assets/images/BankCard/visa.png"
                          class="card-logo"
                      />
                      <img
                          v-else-if="cardType === 'mastercard'"
                          src="@/assets/images/BankCard/mastercard.png"
                          class="card-logo"
                      />
                      <img
                          v-else-if="cardType === 'unknown'"
                          class="card-logo"
                      />
                    </div>
                    <div class="card-number">
                      {{ cardNumber || '#### #### #### ####' }}
                    </div>
                    <div class="card-footer">
                      <div>{{ cardName || 'AD SOYAD' }}</div>
                      <div>{{ expiryMonth || 'MM' }}/{{ expiryYear || 'YY' }}</div>
                    </div>
                  </div>

                  <!-- Arka yüz -->
                  <div class="card-face card-back">
                    <div class="card-stripe"></div>
                    <div class="card-cvv-label">CVV</div>
                    <div class="card-cvv">{{ cvv || '***' }}</div>
                  </div>
                </div>
              </div>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </b-form>
    <b-alert
        show
        variant="info"
        class="mt-4 mb-0 text-center"
    >
      <div class="font-weight-bold mb-3">
        ℹ️ Havale / EFT / IBAN
      </div>

      <div class="mb-3">
        <div class="d-flex justify-content-center align-items-center">
      <span class="font-weight-bold">
        {{ accountName }}
      </span>

          <span
              class="ml-2 copy-icon"
              title="Alıcı adını kopyala"
              @click="copyText(accountName, $event)"
          >
        📋
      </span>
        </div>
      </div>

      <div>
        <div class="d-flex justify-content-center align-items-center">
      <span class="font-weight-bold iban-text">
        {{ iban }}
      </span>

          <span
              class="ml-2 copy-icon"
              title="IBAN'ı kopyala"
              @click="copyText(iban.replace(/\s/g, ''), $event)"
          >
        📋
      </span>
        </div>
      </div>
    </b-alert>
  </div>
</template>

<script>
import PaymentService from "@/service/PaymentService";

export default {
  data() {
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 16 }, (_, i) => {
      const fullYear = currentYear + i;
      const shortYear = fullYear.toString().slice(2); // sadece son 2 hane
      return { value: shortYear, text: shortYear };
    });
    return {
      cardNumber: '',
      cardName: '',
      expiryMonth: '',
      expiryYear: '',
      cvv: '',
      amount: null,
      isCvvFocused: false,
      monthOptions: [
        { value: '', text: 'Ay' },
        { value: '01', text: '01' },
        { value: '02', text: '02' },
        { value: '03', text: '03' },
        { value: '04', text: '04' },
        { value: '05', text: '05' },
        { value: '06', text: '06' },
        { value: '07', text: '07' },
        { value: '08', text: '08' },
        { value: '09', text: '09' },
        { value: '10', text: '10' },
        { value: '11', text: '11' },
        { value: '12', text: '12' },
      ],
      yearOptions: [{ value: '', text: 'Yıl' }, ...years],
      accountName: 'Validem Unlu Mamüller',
      iban: 'TR12 0006 4000 0014 2080 8527 64',
    };
  },
  mounted() {
    const pending = this.$store.state.pendingOrder;
    if (!pending) return;

    const clientId = this.$store.state.clientId;
    const isExpired = !pending.expiresAt || pending.expiresAt < Date.now();
    const isMismatch = pending.clientId !== clientId;

    if (isExpired || isMismatch || !clientId) return;

    if (pending.prefillAmount != null) {
      this.amount = pending.prefillAmount;
      // Tüketildikten sonra yalnızca prefillAmount temizlenir;
      // pendingOrder.clientId ve expiresAt TTL ile yaşamaya devam eder.
      this.$store.commit('consumePendingOrderPrefill');
    }
  },
  computed: {
    cardType() {
      const digits = this.cardNumber.replace(/\s/g, '');
      if (/^4/.test(digits)) return 'visa';
      if (/^5[1-5]/.test(digits)) return 'mastercard';
      return 'unknown';
    },
    formattedAmount() {
      if (this.amount === null || this.amount === undefined || Number.isNaN(this.amount)) return '';

      // Intl ile TR formatı uygula
      return new Intl.NumberFormat('tr-TR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 4
      }).format(this.amount);
    },
  },
  methods: {
    formatCardNumber() {
      let raw = this.cardNumber.replace(/\D/g, '').slice(0, 16); // sadece rakam ve 16 karakter sınırı
      let formatted = raw.replace(/(.{4})/g, '$1 ').trim(); // her 4 hanede boşluk
      this.cardNumber = formatted;
    },
    onlyAllowNumbers(event) {
      const charCode = event.charCode
      if (charCode < 48 || charCode > 57) {
        event.preventDefault()
      }
    },
    onlyAllowNumbersForAmount(event) {
      const char = String.fromCharCode(event.charCode);

      // Rakam, nokta veya virgül haricini engelle
      if (!/[0-9,]/.test(char)) {
        event.preventDefault();
      }
    },
    onInputAmount(value) {
      if (value == null) { this.amount = null; return; }

      let s = String(value).replace(/\./g, ''); // binlik noktaları sil
      s = s.replace(',', '.');                  // virgülü noktaya çevir

      if (s === '' || s === '.') { this.amount = null; return; }

      const num = Number(s);
      this.amount = Number.isFinite(num) ? num : null;
    },
    async submitPayment() {
      const rawNumber = this.cardNumber.replace(/\s/g, '');
      const month = parseInt(this.expiryMonth, 10);
      const year = parseInt(this.expiryYear, 10);
      const cvv = this.cvv;

      const currentYear = new Date().getFullYear();
      const currentYearLast2 = parseInt(currentYear.toString().slice(2));

      // Kart numarası kontrolü
      if (rawNumber.length !== 16 || isNaN(Number(rawNumber))) {
        this.$alert(this, "Lütfen geçerli kart numarası girin.");
        return;
      }

      // Ay kontrolü
      if (isNaN(month) || month < 1 || month > 12) {
        this.$alert(this, "Lütfen geçerli ay bilgisi girin.");
        return;
      }

      // Yıl kontrolü
      if (isNaN(year) || year < currentYearLast2) {
        this.$alert(this, "Lütfen geçerli yıl bilgisi girin.");
        return;
      }

      // CVV kontrolü
      if (cvv.length !== 3 || isNaN(Number(cvv))) {
        this.$alert(this, "Lütfen geçerli cvv bilgisi girin.");
        return;
      }

      try {
        const rawNumber = this.cardNumber.replace(/\s/g, '');
        const month = this.expiryMonth;
        const year = this.expiryYear;
        const cvv = this.cvv;

        const requestData = {
          amount: this.amount,
          shopId: this.$store.state.clientId,
          pan: rawNumber,
          cardName: this.cardName,
          Ecom_Payment_Card_ExpDate_Month: month,
          Ecom_Payment_Card_ExpDate_Year: year,
          cv2: cvv
        };

        const response = await PaymentService.prepareWithCard(requestData);

        // Dinamik form oluştur ve gönder
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = response.gatewayUrl;
        form.style.display = 'none';

        Object.entries(response).forEach(([key, value]) => {
          if (key === 'gatewayUrl') return;
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = value;
          form.appendChild(input);
        });

        document.body.appendChild(form);
        form.submit();

      } catch (err) {
        console.error('Ödeme sırasında hata:', err);
        this.$errorNotification(this, "Ödeme sırasında bir hata oluştu.");
      }
    },

    async copyText(text, event) {
      const target = event.currentTarget;

      try {
        await navigator.clipboard.writeText(text);

        target.style.opacity = '0.4';

        setTimeout(() => {
          target.style.opacity = '1';
        }, 800);
      } catch (error) {
        console.error('Kopyalama işlemi başarısız:', error);
      }
    },
  },
};
</script>

<style scoped>
input:focus {
  outline: none;
  border-color: #3182ce;
  box-shadow: 0 0 0 1px #3182ce;
}

.card-wrapper {
  width: 100%;
  max-width: 400px;
  height: 250px;
  margin: 0 auto;
  perspective: 1000px;
}

.card-inner {
  width: 100%;
  height: 100%;
  position: relative;
  transition: transform 0.8s;
  transform-style: preserve-3d;
}

.card-inner.flipped {
  transform: rotateY(180deg);
}

.card-face {
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  border-radius: 12px;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.25);
  padding: 20px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.card-front {
  background: linear-gradient(to right, #4f46e5, #3b82f6);
  color: white;
}

.card-back {
  background: #1f2937;
  color: white;
  transform: rotateY(180deg);
}

.card-header {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.card-logo {
  height: 30px;
}

.card-number {
  font-size: 1.4rem;
  letter-spacing: 2px;
  font-family: monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.card-footer {
  display: flex;
  justify-content: space-between;
  font-size: 0.9rem;
  margin-top: auto;
}

.card-stripe {
  background: white;
  height: 40px;
  margin-bottom: 20px;
}

.card-cvv-label {
  font-size: 0.8rem;
  margin-bottom: 4px;
}

.card-cvv {
  background: white;
  color: black;
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-family: monospace;
}

input.form-control {
  border: 1px solid #ccc;
  padding: 10px 15px;
  font-size: 1rem;
  transition: border-color 0.3s;
}

input.form-control:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0,123,255,.25);
}

label,
.text-right.font-weight-bold {
  font-size: 0.95rem;
  color: #444;
}

.rounded-pill {
  border-radius: 50px !important;
}

.small-input {
  width: 50px;
  text-align: center;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
}

.expanded-centered-input {
  width: 70px; /* ya da istediğin kadar genişlik */
  height: 40px;
  font-size: 1.05rem;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

@media (max-width: 768px) {
  .small-input {
    width: 100% !important;
    margin-bottom: 0.5rem;
  }

  .card-wrapper {
    height: 200px;
  }

  .card-number {
    font-size: 1.2rem;
    text-align: center;
  }

  .card-logo {
    height: 24px;
  }

  .card-footer {
    justify-content: center;
  }

  .rounded-pill {
    font-size: 0.9rem;
  }
}

/* Chrome, Safari, Edge, Opera */
input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type="number"] {
  -moz-appearance: textfield;
}

.copy-icon {
  cursor: pointer;
  font-size: 18px;
  transition: opacity 0.2s;
  user-select: none;
}

.iban-text {
  letter-spacing: 1px;
  user-select: all;
}
</style>

